import React, { useRef, useState } from 'react';
import { Divider, Button, Row, Col } from 'antd';
import { isNil } from 'lodash';
import moment from 'moment';

import { FullBOUserResponseModel } from 'api/backofficeApiClient';
import PricingAgreementModal, {
  PricingAgreementType,
} from 'components/modals/PricingAgreementModal';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import useScrollAfterRedirect from 'features/account/hooks/useScrollAfterRedirect';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import ConfirmPricingAgreementModal from './ConfirmPricingAgreementModal/ConfirmPricingAgreementModal';

import styles from './PricingAgreement.module.less';

type Props = {
  profile: FullBOUserResponseModel;
  loadFullProfile: (options?: LoadRecruiterDetailsOptions) => Promise<void>;
};

export default function PricingAgreement(props: Props) {
  const { profile, loadFullProfile } = props;
  const [isPricingAgreementModalOpen, setIsPricingAgreementModalOpen] =
    useState(false);
  const [isConfirmPricingAgreementModalOpen, setIsConfirmPricingAgreementOpen] =
    useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useScrollAfterRedirect({ sectionRef });
  const { businessInfo } = profile;

  if (!businessInfo) return null;

  const { priceAgreement } = businessInfo;

  const onAgreementSent = async () => {
    await loadFullProfile();
  };

  return (
    <>
      {isConfirmPricingAgreementModalOpen && (
        <ConfirmPricingAgreementModal
          onAgreementSent={loadFullProfile}
          onClose={() => setIsConfirmPricingAgreementOpen(false)}
        />
      )}

      {isPricingAgreementModalOpen && (
        <PricingAgreementModal
          agreementType={PricingAgreementType.Change}
          onAgreementSent={onAgreementSent}
          onClose={() => setIsPricingAgreementModalOpen(false)}
        />
      )}

      <BasePageCard
        ref={sectionRef}
        title="Pricing agreement"
        className="stretch-card"
        icons={<span className="icon icon-pricing-agreement"></span>}
        subTitle={
          !!priceAgreement && (
            <>
              {!priceAgreement.canBeConfirmed &&
                priceAgreement.hasChangeRequest && (
                  <div className={styles.subTitle}>
                    <span className="icon icon-attention-filled" />
                    <span>Changes sent, waiting for admin response</span>
                  </div>
                )}

              {priceAgreement.canBeConfirmed &&
                !priceAgreement.hasChangeRequest && (
                  <Row gutter={[4, 4]} justify="end">
                    <Col>
                      <Button
                        onClick={() => setIsConfirmPricingAgreementOpen(true)}
                        icon={<span className="icon icon-check-outlined" />}
                        className="with-icon mobile-icon-only"
                        size="large"
                        type="primary"
                      >
                        Confirm
                      </Button>
                    </Col>
                  </Row>
                )}
            </>
          )
        }
      >
        {!priceAgreement && (
          <div className="content-wrapper">
            <BaseSectionMessage
              title="Info"
              type="info"
              message={
                <span>
                  Waiting for administrator response. You can confirm them after
                  in this section.
                </span>
              }
            />
          </div>
        )}

        {!!priceAgreement && (
          <>
            <div className="content-wrapper">
              <Row gutter={[4, 4]} className="titled-sheet">
                {priceAgreement.w2InfoList?.map((set, index) => (
                  <React.Fragment
                    key={`price-agreement-${set.w2Price}-${index}`}
                  >
                    <Col xs={24} md={12}>
                      <div className="title">W2 price:</div>
                      <div>{set.w2Price?.toFixed(2)}%</div>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className="title">W2 states:</div>
                      <div>
                        {set.placementStates
                          ?.map((state) => state.name)
                          .join(', ')}
                      </div>
                    </Col>

                    {(set.workCodes || [])?.length > 0 && (
                      <Col xs={24} md={12}>
                        <div className="title">WC Codes:</div>
                        <div>
                          {set.workCodes?.map((code) => code).join(', ')}
                        </div>
                      </Col>
                    )}
                  </React.Fragment>
                ))}

                {!isNil(priceAgreement.icPrice) && (
                  <Col xs={24} md={12}>
                    <div className="title">IC price:</div>
                    <div>{priceAgreement.icPrice.toFixed(2)}%</div>
                  </Col>
                )}
              </Row>
            </div>

            {priceAgreement.isConfirmation && (
              <>
                <Divider className="no-margin" />

                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24} md={12}>
                      <div className="title">Customer signature:</div>
                      <div>{priceAgreement.confirmationSignature}</div>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className="title">Date signed:</div>
                      <div>
                        {moment(priceAgreement.confirmationDate).format(
                          'MM/DD/YYYY',
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div className={styles.note}>
                    <span className="icon icon-check-outlined" />

                    <span>
                      You have reviewed and agreed to myBasePay pricing terms.
                    </span>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </BasePageCard>
    </>
  );
}
